import {
  ArrowRight,
  Check,
  FilePlus,
  FolderPlus,
  FolderTree,
  Loader2,
  Paperclip,
  Scissors,
  Search,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { TimelineStep, StepTool } from '@/stores/useStewardStore'
import type { StewardStageType } from '@/lib/api'
import { StewardThinkingBlock } from './StewardThinkingBlock'

interface Props {
  steps: TimelineStep[]
  /** True while the turn is still streaming — the running step pulses and its
   *  thinking auto-expands. False once the answer lands (everything folds). */
  live?: boolean
}

/**
 * The per-node activity timeline for one steward turn. Replaces the old row of
 * status pills with a vertical, sequential log: each node that ran is a row
 * showing its state (running spinner → done check), any tool it invoked
 * (activated → result), and its extended thinking nested beneath. While the
 * turn streams the active node is live; when the final answer displays every
 * row folds to a compact one-liner.
 *
 * Pure conversation stays conversational: the `route→converse` and `converse`
 * nodes render NO row (chat should feel like chat) — but their deep-mode
 * thinking still surfaces as a bare block so nothing is hidden.
 */
export function StewardTimeline({ steps, live = false }: Props) {
  const rows = steps.map((step) => ({ step, desc: describe(step) }))
  const hasAnything = rows.some((r) => r.desc || r.step.thinking.trim())
  if (!hasAnything) return null

  // Visible (row-bearing) steps need the connecting rail between them; the last
  // visible row drops its tail so the line doesn't dangle.
  const lastVisibleIdx = rows.reduce((acc, r, i) => (r.desc ? i : acc), -1)

  return (
    <div className="mb-3 flex flex-col">
      {rows.map(({ step, desc }, i) => {
        if (!desc) {
          // A hidden node (converse) — surface its thinking bare, no row.
          return step.thinking.trim() ? (
            <StewardThinkingBlock
              key={i}
              thinking={step.thinking}
              isStreaming={live && step.status === 'running'}
            />
          ) : null
        }
        return (
          <TimelineRow
            key={i}
            step={step}
            desc={desc}
            live={live}
            showTail={i < lastVisibleIdx}
          />
        )
      })}
    </div>
  )
}

const ICON = 'h-3 w-3 shrink-0'

function TimelineRow({
  step,
  desc,
  live,
  showTail,
}: {
  step: TimelineStep
  desc: StepDescription
  live: boolean
  showTail: boolean
}) {
  const running = step.status === 'running'
  const hasThinking = step.thinking.trim().length > 0

  return (
    <div className="flex gap-2.5">
      {/* Rail: state marker + connecting line down to the next row. */}
      <div className="flex flex-col items-center pt-0.5">
        {running ? (
          <Loader2 className="h-3 w-3 animate-spin text-amber-500" />
        ) : (
          <span
            className={cn(
              'flex h-3 w-3 items-center justify-center rounded-full',
              desc.tone === 'good'
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <Check className="h-2 w-2" strokeWidth={3} />
          </span>
        )}
        {showTail && <span className="mt-1 w-px flex-1 bg-border/60" />}
      </div>

      {/* Body: label, tool lines, nested thinking. */}
      <div className="min-w-0 flex-1 pb-2.5">
        <div
          className={cn(
            'flex items-center gap-1.5 text-[12px] font-light',
            running ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {desc.icon}
          <span className="truncate">{desc.label}</span>
        </div>

        {step.tools.map((t, i) => (
          <ToolLine key={i} tool={t} />
        ))}

        {hasThinking && (
          <div className="mt-1">
            <StewardThinkingBlock
              thinking={step.thinking}
              isStreaming={live && running}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/** One tool invocation inside a step: activated (spinner) → result (check + count). */
function ToolLine({ tool }: { tool: StepTool }) {
  const running = tool.status === 'running'
  const base = tool.label || tool.tool
  const label = running
    ? `${base}…`
    : tool.count != null
      ? `${base} · ${tool.count} result${tool.count === 1 ? '' : 's'}`
      : base

  return (
    <div className="mt-1 flex items-center gap-1.5 text-[11px] font-light text-muted-foreground/80">
      {running ? (
        <Loader2 className="h-2.5 w-2.5 animate-spin text-muted-foreground/70" />
      ) : (
        <Search className="h-2.5 w-2.5 shrink-0" />
      )}
      <span className="truncate">{label}</span>
    </div>
  )
}

// ── Per-stage label / icon / tone ──────────────────────────────────────────

interface StepDescription {
  icon: ReactNode
  label: string
  tone: 'neutral' | 'good'
}

function describe(step: TimelineStep): StepDescription | null {
  const ctrl = step.output
  // A skipped node has no row (and in practice never even gets a step — skipped
  // nodes don't emit `_start`). Defensive.
  if (ctrl?.skipped) return null
  const out = ctrl?.output ?? {}
  const running = step.status === 'running'

  switch (step.stage) {
    case 'ingest': {
      if (running) return { icon: <Paperclip className={ICON} />, label: 'Reading the files', tone: 'neutral' }
      const filed = Array.isArray(out.filed) ? out.filed : []
      if (filed.length === 0) return null   // passthrough turn (no files) — no row
      return {
        icon: <Paperclip className={ICON} />,
        label: filed.length === 1 ? 'Filed the attachment' : `Filed ${filed.length} attachments`,
        tone: 'good',
      }
    }
    case 'route': {
      const intent = str(out.intent)
      // Pure conversation shows no row — chat should feel like chat.
      if (!running && intent === 'converse') return null
      return {
        icon: <ArrowRight className={ICON} />,
        label: running ? 'Routing' : intent ? `Routed → ${intent}` : 'Routed',
        tone: 'neutral',
      }
    }
    case 'place': {
      if (running) return { icon: <FolderTree className={ICON} />, label: 'Finding a home', tone: 'neutral' }
      const trail = [str(out.area), str(out.category)].filter(Boolean).join(' › ')
      return {
        icon: <FolderTree className={ICON} />,
        label: trail ? `Placed: ${trail}` : 'Needs a new home',
        tone: 'neutral',
      }
    }
    case 'compose': {
      if (running) return { icon: <FilePlus className={ICON} />, label: 'Filing', tone: 'neutral' }
      const ok = out.ok !== false
      const name = str(out.folder_name) || str(out.title)
      return {
        icon: <FilePlus className={ICON} />,
        label: ok ? (name ? `Filed: ${name}` : 'Filed') : 'Filing failed',
        tone: ok ? 'good' : 'neutral',
      }
    }
    case 'retrieve': {
      if (running) return { icon: <Search className={ICON} />, label: 'Reading the map', tone: 'neutral' }
      // Pure conversation (no index read) — no row; the reply itself is the message.
      const tools = Array.isArray(out.tools_used) ? out.tools_used : []
      if (tools.length === 0) return null
      const paths = Array.isArray(out.surfaced_paths) ? out.surfaced_paths : []
      if (paths.length > 1) return { icon: <Search className={ICON} />, label: `Pulled ${paths.length} from the map`, tone: 'good' }
      if (paths.length === 1) return { icon: <Search className={ICON} />, label: 'Surfaced the one that fits', tone: 'good' }
      return { icon: <Search className={ICON} />, label: 'Answered from the map', tone: 'good' }
    }
    case 'commission':
      return {
        icon: <FolderPlus className={ICON} />,
        label: running ? 'Setting up structure' : 'Structure commission',
        tone: 'neutral',
      }
    case 'commonplace_keeper': {
      // The surgeon. Its reply (the plan / confirmation) is the message; the row marks the work.
      if (running) return { icon: <Scissors className={ICON} />, label: 'Working the map', tone: 'neutral' }
      const phase = str(out.phase)
      if (phase === 'executed') return { icon: <Scissors className={ICON} />, label: 'Operated on the map', tone: 'good' }
      if (phase === 'plan') return { icon: <Scissors className={ICON} />, label: 'Drew up the plan', tone: 'good' }
      if (phase === 'audit') return { icon: <Scissors className={ICON} />, label: 'Swept the structure', tone: 'good' }
      return null   // talk / exit — the reply itself carries the turn
    }
    default:
      return assertNever(step.stage)
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function assertNever(value: StewardStageType): null {
  void value
  return null
}
